import type { FeatureToggles } from '@makemydeal/dr-platform-shared';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import { bootstrapActionTypes } from '@makemydeal/dr-shared-store';
import { payloadUtils } from '@makemydeal/dr-shared-ui-utils';
import type { MiddlewareManager, TradeInConfig } from '@makemydeal/activities-tradein';
import { Actions } from '@makemydeal/activities-tradein';
import { configureTradeIn } from './config';

import { getConfigStaticImages, getServicesBff } from '../../selectors/salesCheckinAppSelectors';
import type { IConfigObj } from '@makemydeal/dr-common-utils';
import type { TradeInServicesConfig } from '../../types/common';
import type { SalesCheckinRootState } from '@makemydeal/dr-sales-checkin-store';
import { Actions as SCIActions } from '@makemydeal/dr-sales-checkin-store';
import { hostActions } from './hostActions';

export let cachedTradeInMiddlewareMgr: MiddlewareManager | undefined;

export const tradeInMiddleware: ReduxMiddleware<SalesCheckinRootState> =
    (store: ReduxStore<SalesCheckinRootState>) => (next: ReduxNext) => (action: AnyFSA) => {
        switch (action.type) {
            case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
                // Initialize trade-in on bootstrap
                const state = store.getState();
                const bff = getServicesBff(state);
                const featureToggles: FeatureToggles = payloadUtils.getFeatureTogglesFromInitPayload(action.payload);
                const kbbTradeInApiKey = payloadUtils.getConfigFromInitPayload<TradeInServicesConfig>(action.payload)?.services
                    ?.kbbTradeInApiKey;

                const staticImages = getConfigStaticImages(state) as IConfigObj;

                const tradeInConfig: TradeInConfig = {
                    services: {
                        bff
                    },
                    featureToggles,
                    staticImages,
                    kbbTradeInApiKey,
                    hostActions: {
                        ...hostActions,
                        relayTradeInEvents: (cdlPayload: any) => ({
                            type: 'CDL_EVENT',
                            payload: cdlPayload
                        })
                    }
                };

                action = {
                    ...action,
                    meta: {
                        ...action.meta,
                        tradeInConfig
                    }
                };

                // Initialize & cache middleware manager if not already present
                cachedTradeInMiddlewareMgr = configureTradeIn(tradeInConfig);
                break;
            }
        }

        // Delegate to initialized manager middleware if available
        if (cachedTradeInMiddlewareMgr) {
            const result = cachedTradeInMiddlewareMgr.middleware(store)(next)(action);

            // Once a deal refresh has been reduced, tell the trade-in activity the deal changed via a
            // generic action carrying the refreshed deal. The activity decides what to do from its own
            // state (e.g. re-mapping trade-in data or resetting a now-stale in-memory route) so the host
            // stays out of trade-in internals.
            if (action.type === SCIActions.FETCH_DEAL_SUCCESS) {
                store.dispatch({ type: Actions.REFRESH_TRADE_IN, payload: action.payload });
            }

            return result;
        } else {
            return next(action);
        }
    };
